import { eq, useLiveQuery } from '@tanstack/react-db';
import { createFileRoute } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CollectionDialog } from '@/components/catalog/collection-dialog';

import {
  DataTable,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import {
  type CollectionRecord,
  type CollectionRuleRecord,
  collectionRulesCollection,
  collectionsCollection,
} from '@/lib/collections';

/**
 * Collections — the merchandising groupings a storefront browses by.
 *
 * Every collection in the catalog is rule-based: membership is evaluated from
 * `collection_rules` rather than stored in `collection_products`. That is why
 * this table shows a rule count and a match mode instead of a product count —
 * the number of products in a smart collection is not a stored fact, and
 * showing one would mean running the rules for every row on every render.
 */
export const Route = createFileRoute('/collections/')({
  loader: async () => {
    await Promise.all([collectionsCollection.preload(), collectionRulesCollection.preload()]).catch(
      () => undefined,
    );
  },
  component: CollectionsPage,
});

interface CollectionRow {
  id: string;
  name: string;
  slug: string;
  kind: string;
  match: string;
  rules: number;
  position: number | null;
  active: boolean;
}

const column = dataTableColumnHelper<CollectionRow>();

const columns = column.columns([
  column.accessor('name', {
    header: 'Collection',
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{row.original.name}</div>
        <div className="text-muted-foreground truncate text-xs">{row.original.slug}</div>
      </div>
    ),
  }),

  column.accessor('kind', {
    header: 'Type',
    cell: ({ getValue }) => (
      <Badge variant="outline" className="font-normal capitalize">
        {getValue() || 'manual'}
      </Badge>
    ),
  }),

  column.accessor('rules', {
    header: 'Rules',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ row }) =>
      row.original.rules === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        // "2 rules, all must match" is the whole definition of a smart
        // collection, so the match mode belongs next to the count.
        <span>
          {row.original.rules}
          <span className="text-muted-foreground"> · {row.original.match}</span>
        </span>
      ),
  }),

  column.accessor('position', {
    header: 'Order',
    meta: { className: 'text-right tabular-nums' },
    cell: ({ getValue }) => getValue() ?? '—',
  }),

  column.accessor('active', {
    header: 'Status',
    cell: ({ getValue }) => (
      <Badge variant={getValue() ? 'default' : 'secondary'}>
        {getValue() ? 'Active' : 'Hidden'}
      </Badge>
    ),
  }),

  column.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'text-right' },
    cell: ({ row, table }) => {
      const onEdit = table.options.meta?.onEdit;

      return (
        <Button variant="ghost" size="sm" onClick={() => onEdit?.(row.original.id)}>
          Edit
        </Button>
      );
    },
  }),
]);

function CollectionsPage() {
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';
  const canEdit = can('catalog.manage');
  const [globalFilter, setGlobalFilter] = useState('');
  /** `null` means "create"; a string is the id being edited. */
  const [editing, setEditing] = useState<string | null | undefined>(undefined);

  const collections = useLiveQuery(
    (q) =>
      q
        .from({ collection: collectionsCollection })
        .where(({ collection }) => eq(collection.account_id, accountId)),
    [accountId],
  );

  const rules = useLiveQuery(
    (q) =>
      q
        .from({ rule: collectionRulesCollection })
        .where(({ rule }) => eq(rule.account_id, accountId)),
    [accountId],
  );

  const rows = useMemo<CollectionRow[]>(() => {
    const ruleCounts = new Map<string, number>();

    for (const rule of (rules.data ?? []) as CollectionRuleRecord[]) {
      ruleCounts.set(rule.collection_id, (ruleCounts.get(rule.collection_id) ?? 0) + 1);
    }

    return ((collections.data ?? []) as CollectionRecord[]).map((collection) => ({
      id: collection.id,
      name: collection.name,
      slug: collection.slug,
      kind: collection.kind ?? '',
      match: collection.rules_match ?? 'all',
      rules: ruleCounts.get(collection.id) ?? 0,
      position: collection.position,
      active: collection.active ?? false,
    }));
  }, [collections.data, rules.data]);

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns,
    data: rows,
    meta: { onEdit: (id: string) => setEditing(id) },
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 25 },
      // Collections are ordered by hand for the storefront, so that is the
      // order to open in -- alphabetical would hide the merchandiser's intent.
      sorting: [{ id: 'position', desc: false }],
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Collections</h1>
          <p className="text-muted-foreground text-sm">
            {collections.isLoading
              ? 'Syncing…'
              : `${rows.length} collection${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter collections…"
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="w-64"
          />

          {canEdit && (
            <Button size="sm" onClick={() => setEditing(null)}>
              <Plus className="size-4" />
              New collection
            </Button>
          )}
        </div>
      </div>

      <DataTable
        table={table}
        columnCount={columns.length}
        isLoading={collections.isLoading}
        onRowActivate={(row) => setEditing(row.id)}
        emptyMessage={globalFilter ? 'No collections match that filter.' : 'No collections yet.'}
      />

      {rows.length > 25 && <DataTablePagination table={table} total={rows.length} />}

      {/*
        Keyed by the record being edited, so the dialog's form state is rebuilt
        rather than carried over from whichever collection was opened last.
      */}
      {editing !== undefined && (
        <CollectionDialog
          key={editing ?? 'new'}
          open
          onOpenChange={(next) => !next && setEditing(undefined)}
          collection={
            editing
              ? (collections.data as CollectionRecord[])?.find((c) => c.id === editing)
              : undefined
          }
        />
      )}
    </div>
  );
}
